import { Chart, ChartConfiguration, Plugin } from 'chart.js'

export type DotData = { name: string; score: number; color: string }
export type DotsPluginOptions = { dotRadius?: number; showLabels?: boolean }

export const AUDIO_QUALITY_BENCHMARK_BINS = [
  { label: '0.58', count: 1 },
  { label: '0.60', count: 2 },
  { label: '0.62', count: 2 },
  { label: '0.64', count: 4 },
  { label: '0.66', count: 5 },
  { label: '0.68', count: 6 },
  { label: '0.70', count: 13 },
  { label: '0.72', count: 12 },
  { label: '0.74', count: 21 },
  { label: '0.76', count: 29 },
  { label: '0.78', count: 18 },
  { label: '0.80', count: 12 },
  { label: '0.82', count: 1 },
]

export const AUDIO_QUALITY_CHART_DATA: ChartConfiguration<'bar'>['data'] = {
  labels: AUDIO_QUALITY_BENCHMARK_BINS.map((bin) => bin.label),
  datasets: [
    {
      label: 'Audio Quality Distribution',
      data: AUDIO_QUALITY_BENCHMARK_BINS.map((bin) => bin.count),
      backgroundColor: '#E8A838',
      borderColor: '#E8A838',
      borderWidth: 0,
      barPercentage: 0.97,
      categoryPercentage: 1,
      hoverBackgroundColor: '#E8A838',
      hoverBorderColor: '#E8A838',
    },
  ],
}

export function createDotsPlugin(dots: DotData[], options: DotsPluginOptions = {}): Plugin<'bar'> {
  const { dotRadius = 6, showLabels = false } = options
  const bins = AUDIO_QUALITY_BENCHMARK_BINS
  let hoveredDot: { name: string; x: number; y: number } | null = null

  const resolvedDots = dots
    .map((dot) => {
      const exactPosition = (dot.score - 0.58) / 0.02
      const binIndex = Math.floor(exactPosition)
      const fractionInBin = exactPosition - binIndex
      const clampedIndex = Math.max(0, Math.min(binIndex, bins.length - 1))
      const xValue = clampedIndex - 0.5 + fractionInBin
      return { ...dot, xValue }
    })
    .sort((a, b) => a.score - b.score)

  function getPixelForFractionalValue(xScale: any, xValue: number): number {
    const floor = Math.floor(xValue)
    const ceil = Math.ceil(xValue)
    const pxFloor = xScale.getPixelForValue(floor)
    if (floor === ceil) return pxFloor
    const pxCeil = xScale.getPixelForValue(ceil)
    return pxFloor + (pxCeil - pxFloor) * (xValue - floor)
  }

  const minSpacing = dotRadius * 3

  function placeDots(chart: Chart): { px: number; py: number; name: string; color: string }[] {
    const xScale = chart.scales['x']
    const baseY = chart.chartArea.bottom - 10
    const placed: { px: number; py: number; name: string; color: string }[] = []

    resolvedDots.forEach((dot) => {
      const idealPx = getPixelForFractionalValue(xScale, dot.xValue)
      let bestPx = idealPx
      let bestPy = baseY
      let found = false

      for (let row = 0; row < 20 && !found; row++) {
        const py = baseY - row * minSpacing
        const offsets = row === 0 ? [0] : [-minSpacing * 0.4, minSpacing * 0.4]
        for (const dx of offsets) {
          const px = idealPx + dx
          const collides = placed.some((p) => Math.sqrt((p.px - px) ** 2 + (p.py - py) ** 2) < minSpacing)
          if (!collides) {
            bestPx = px
            bestPy = py
            found = true
            break
          }
        }
      }

      placed.push({ px: bestPx, py: bestPy, name: dot.name, color: dot.color })
    })

    return placed
  }

  const labelFont = "11px 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif"
  const labelPadding = { h: 6, v: 3 }
  const leaderLineWidth = 1.2
  const labelAreaTopMargin = 12
  const dashPatterns: number[][] = [
    [6, 3],
    [2, 3],
    [10, 4],
    [4, 2, 1, 2],
  ]
  const elbowDotRadius = 2

  type LabelBox = { x: number; y: number; w: number; h: number; name: string; dotX: number; dotY: number; color: string }

  function deconflictLabels(labels: LabelBox[], dotPositions: { px: number; py: number }[], chartLeft: number, chartRight: number, minY: number) {
    const maxIterations = 80
    const verticalPush = 2
    const dotObstacleRadius = dotRadius + 4

    for (let iter = 0; iter < maxIterations; iter++) {
      let anyOverlap = false

      // Label-vs-label deconfliction
      for (let i = 0; i < labels.length; i++) {
        for (let j = i + 1; j < labels.length; j++) {
          const a = labels[i]
          const b = labels[j]
          const overlapX = a.w / 2 + b.w / 2 + 4 - Math.abs(a.x - b.x)
          const overlapY = a.h / 2 + b.h / 2 + 2 - Math.abs(a.y - b.y)

          if (overlapX > 0 && overlapY > 0) {
            anyOverlap = true
            const pushY = verticalPush
            if (a.y <= b.y) {
              a.y -= pushY
              b.y += pushY * 0.3
            } else {
              b.y -= pushY
              a.y += pushY * 0.3
            }

            const pushX = overlapX * 0.15
            if (a.x <= b.x) {
              a.x -= pushX
              b.x += pushX
            } else {
              b.x -= pushX
              a.x += pushX
            }
          }
        }
      }

      // Label-vs-dot deconfliction
      for (const label of labels) {
        for (const dot of dotPositions) {
          const closestX = Math.max(label.x - label.w / 2, Math.min(dot.px, label.x + label.w / 2))
          const closestY = Math.max(label.y - label.h / 2, Math.min(dot.py, label.y + label.h / 2))
          const dist = Math.sqrt((closestX - dot.px) ** 2 + (closestY - dot.py) ** 2)

          if (dist < dotObstacleRadius) {
            anyOverlap = true
            label.y -= verticalPush * 1.5
            if (Math.abs(label.x - dot.px) < label.w / 2) {
              label.x += label.x <= dot.px ? -verticalPush : verticalPush
            }
          }
        }
      }

      // Clamp within chart bounds
      for (const label of labels) {
        label.x = Math.max(chartLeft + label.w / 2, Math.min(chartRight - label.w / 2, label.x))
        label.y = Math.max(minY, label.y)
      }

      if (!anyOverlap) break
    }
  }

  return {
    id: 'dotsOverlay',
    afterDatasetsDraw(chart: Chart) {
      const { ctx } = chart
      const positions = placeDots(chart)

      // Draw dots
      positions.forEach(({ px, py, name, color }) => {
        const isHovered = !showLabels && hoveredDot?.name === name && Math.abs(hoveredDot.x - px) < 1 && Math.abs(hoveredDot.y - py) < 1
        const r = isHovered ? dotRadius * 1.3 : dotRadius
        ctx.save()
        ctx.beginPath()
        ctx.arc(px, py, r, 0, Math.PI * 2)
        ctx.fillStyle = color
        ctx.fill()
        if (isHovered) {
          ctx.beginPath()
          ctx.arc(px, py, r, 0, Math.PI * 2)
          ctx.fillStyle = 'rgba(0, 0, 0, 0.15)'
          ctx.fill()
        }
        ctx.restore()
      })

      // Leader-line labels mode
      if (showLabels && positions.length > 0) {
        ctx.save()
        ctx.font = labelFont

        const highestDotY = Math.min(...positions.map((p) => p.py))
        const labelStartY = highestDotY - dotRadius - labelAreaTopMargin

        const labelBoxes: LabelBox[] = positions.map(({ px, py, name, color }) => {
          const textWidth = ctx.measureText(name).width
          const w = textWidth + labelPadding.h * 2
          const h = 14 + labelPadding.v * 2
          return { x: px, y: labelStartY, w, h, name, dotX: px, dotY: py, color }
        })

        deconflictLabels(labelBoxes, positions, chart.chartArea.left, chart.chartArea.right, chart.chartArea.top + 10)

        // Build orthogonal leader line paths
        type LeaderLine = { segments: { x1: number; y1: number; x2: number; y2: number }[]; color: string; dashIndex: number }

        const usedHorizontalBands: { y: number; xMin: number; xMax: number }[] = []
        const horizontalSpacing = 10
        const leaderLines: LeaderLine[] = []
        let dashCounter = 0

        labelBoxes.forEach((label) => {
          const labelBottomY = label.y + label.h / 2
          const dotTopY = label.dotY - dotRadius
          const dx = Math.abs(label.x - label.dotX)
          const dy = Math.abs(labelBottomY - dotTopY)
          const currentDash = dashCounter++ % dashPatterns.length

          if (dy <= 6 && dx <= 6) return

          if (dx <= 2) {
            leaderLines.push({
              color: label.color,
              dashIndex: currentDash,
              segments: [{ x1: label.dotX, y1: dotTopY, x2: label.x, y2: labelBottomY }],
            })
            return
          }

          const xMin = Math.min(label.x, label.dotX)
          const xMax = Math.max(label.x, label.dotX)
          const lShapeY = labelBottomY

          const lShapeCollides = usedHorizontalBands.some(
            (used) => Math.abs(used.y - lShapeY) < horizontalSpacing && xMin < used.xMax + 2 && xMax > used.xMin - 2
          )

          if (!lShapeCollides) {
            usedHorizontalBands.push({ y: lShapeY, xMin, xMax })
            leaderLines.push({
              color: label.color,
              dashIndex: currentDash,
              segments: [
                { x1: label.dotX, y1: dotTopY, x2: label.dotX, y2: lShapeY },
                { x1: label.dotX, y1: lShapeY, x2: label.x, y2: lShapeY },
              ],
            })
            return
          }

          let elbowY = dotTopY + (labelBottomY - dotTopY) * 0.5
          for (let attempt = 0; attempt < 15; attempt++) {
            const collides = usedHorizontalBands.some((used) => Math.abs(used.y - elbowY) < horizontalSpacing && xMin < used.xMax + 2 && xMax > used.xMin - 2)
            if (!collides) break
            elbowY -= horizontalSpacing
          }

          usedHorizontalBands.push({ y: elbowY, xMin, xMax })
          leaderLines.push({
            color: label.color,
            dashIndex: currentDash,
            segments: [
              { x1: label.dotX, y1: dotTopY, x2: label.dotX, y2: elbowY },
              { x1: label.dotX, y1: elbowY, x2: label.x, y2: elbowY },
              { x1: label.x, y1: elbowY, x2: label.x, y2: labelBottomY },
            ],
          })
        })

        // Draw leader lines
        leaderLines.forEach((line) => {
          ctx.save()
          ctx.strokeStyle = line.color
          ctx.lineWidth = leaderLineWidth
          ctx.setLineDash(dashPatterns[line.dashIndex])

          line.segments.forEach((seg, i) => {
            ctx.beginPath()
            ctx.moveTo(seg.x1, seg.y1)
            ctx.lineTo(seg.x2, seg.y2)
            ctx.stroke()

            if (i > 0) {
              ctx.setLineDash([])
              ctx.beginPath()
              ctx.arc(seg.x1, seg.y1, elbowDotRadius, 0, Math.PI * 2)
              ctx.fillStyle = line.color
              ctx.fill()
              ctx.setLineDash(dashPatterns[line.dashIndex])
            }
          })

          ctx.restore()
        })

        // Draw label backgrounds and text
        labelBoxes.forEach((label) => {
          const boxX = label.x - label.w / 2
          const boxY = label.y - label.h / 2

          ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
          ctx.beginPath()
          ctx.roundRect(boxX, boxY, label.w, label.h, 4)
          ctx.fill()

          ctx.fillStyle = '#fff'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(label.name, label.x, label.y)
        })

        ctx.restore()
      }

      // Hover tooltip (only when labels are hidden)
      if (!showLabels && hoveredDot) {
        ctx.save()
        const fontSize = 12
        ctx.font = `${fontSize}px 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif`
        const textWidth = ctx.measureText(hoveredDot.name).width
        const padding = 8
        const boxWidth = textWidth + padding * 2
        const boxHeight = fontSize + padding * 2
        let boxX = hoveredDot.x - boxWidth / 2
        const boxY = hoveredDot.y - dotRadius - boxHeight - 8
        if (boxX < chart.chartArea.left) boxX = chart.chartArea.left
        if (boxX + boxWidth > chart.chartArea.right) boxX = chart.chartArea.right - boxWidth
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
        ctx.beginPath()
        ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 4)
        ctx.fill()
        ctx.fillStyle = '#fff'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(hoveredDot.name, boxX + boxWidth / 2, boxY + boxHeight / 2)
        ctx.restore()
      }
    },
    afterEvent(chart: Chart, args: any) {
      if (showLabels) return
      const event = args.event
      if (event.type !== 'mousemove') return

      const mouseX = event.x
      const mouseY = event.y
      const positions = placeDots(chart)
      const match = positions.find(({ px, py }) => Math.sqrt((mouseX - px) ** 2 + (mouseY - py) ** 2) <= dotRadius + 4)
      const found = match ? { name: match.name, x: match.px, y: match.py } : null

      if (found?.name !== hoveredDot?.name) {
        hoveredDot = found
        args.changed = true
      }
    },
  }
}
